import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  alpha,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography
} from '@material-ui/core';
import { DeleteSweep } from '@material-ui/icons';
import { AppSnackbar, CardTitle } from '@sierralabs/react-material-ui';

import { User } from '../../user/User.interface';
import { useApiDelete } from '../api/Api.hooks';
import { formatDate } from '../formatter';

const StyledCard = styled(Card)`
  .button {
    color: ${p => p.theme.palette.error.main};
    border: 1px solid ${p => alpha(p.theme.palette.error.main, 0.5)};
    &:hover {
      border: 1px solid ${p => p.theme.palette.error.main};
      background-color: ${p =>
        alpha(p.theme.palette.error.main, p.theme.palette.action.hoverOpacity)};
      // Reset on touch devices, it doesn't add specificity
      @media (hover: none) {
        background-color: 'transparent';
      }
    }
  }
  .last-modified {
    margin-top: 12px;
    padding-left: 6px;
    display: flex;
    flex-direction: column;
    color: ${p => p.theme.palette.error.main};
    .modified {
      padding-bottom: 4px;
    }
  }
`;

interface DeleteEntity {
  id: number;
  deleted: boolean;
  modified?: Date;
  modifiedBy?: User;
}

export interface DeleteEntityCardProps {
  title: string;
  apiPath: string;
  entity: DeleteEntity;
}

/**
 * @param apiPath the REST API endpoint path for the entity
 */
const DeleteEntityCard: React.FC<DeleteEntityCardProps> = ({
  title,
  apiPath,
  ...props
}) => {
  const [entity, setEntity] = useState(props.entity);
  const {
    deletedEntity,
    deletingEntity,
    deleteError,
    deleteEntity
  } = useDeleteEntity(apiPath, entity.id);
  useEffect(() => {
    if (deletedEntity) {
      setEntity(deletedEntity);
    }
  }, [deletedEntity]);
  return (
    <StyledCard>
      <CardTitle>
        <DeleteSweep />
        Delete {title}
      </CardTitle>
      <CardContent>
        <Button
          variant='outlined'
          className='button'
          disabled={deletingEntity}
          onClick={() => {
            // handles undelete
            deleteEntity(Boolean(entity.deleted));
          }}
          endIcon={deletingEntity && <CircularProgress size={16} />}
        >
          {entity.deleted ? 'Undelete' : 'Delete'} {title}
        </Button>
        <div className='last-modified'>
          {entity.deleted && entity.modified && (
            <Typography variant='caption' className='modified'>
              Last Modified: {formatDate(entity.modified)}
            </Typography>
          )}
          {entity.deleted && entity.modifiedBy && (
            <Typography variant='caption' className='modified-by'>
              Modified By: {entity.modifiedBy.firstName}{' '}
              {entity.modifiedBy.lastName}
            </Typography>
          )}
        </div>
      </CardContent>
      {deleteError && (
        <AppSnackbar open severity='error'>
          Delete Error {deleteError.message}
        </AppSnackbar>
      )}
      {deletedEntity && !deletedEntity.deleted && (
        <AppSnackbar open severity='success'>
          Undeleted {title}
        </AppSnackbar>
      )}
    </StyledCard>
  );
};

export default DeleteEntityCard;

const useDeleteEntity = (apiPath: string, id: number) => {
  const { data, isLoading, error, del } = useApiDelete<DeleteEntity>(
    `${apiPath}/${id}`
  );
  const deleteEntity = useCallback(
    (undelete?: boolean) => {
      // don't pass undelete query if deleting
      undelete ? del({ undelete }) : del();
    },
    [del]
  );
  return {
    deletedEntity: data,
    deletingEntity: isLoading,
    deleteError: error,
    deleteEntity
  };
};
